// Settings → Billing tab.
//
// The retailer's TKOS SUBSCRIPTION (GoCardless): plan, seats, trial/next
// charge, Direct Debit mandate setup, and invoice history. Distinct from the
// "Payments" tab (Stripe Connect + commission, i.e. the retailer charging THEIR
// customers). Owner-only (registered with TAB_PERM["billing"] = "financials").
//
// Backend (all live, built in Phase 1/2):
//   GET  /api/subscription/status    — plan, seats, billing_status, mandate, trial, next charge
//   GET  /api/subscription/invoices  — this retailer's subscription invoices
//   POST /api/subscription/mandate/start — returns { authorisationUrl } (GoCardless hosted DD flow)
// On return, the callback redirects to #/settings?tab=billing&mandate=connected|cancelled|error.

import { ApiError } from "@tkos/api-client";
import { iconMarkup, type IconName } from "@tkos/shared-ui";
import type { SettingsTab } from "../types";
import type { PageContext } from "../../../shell/types";

import "./billing.css";

// ── Types ──────────────────────────────────────────────────────────────────
interface PlanInfo { key: string; label: string; monthlyPence: number; }
interface SubscriptionView {
  planKey: string;
  seatCount: number;
  billingCadence: string;
  billingStatus: string;
  mandateConnected: boolean;
  routedFeeEnabled: boolean;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  nextChargeDate: string | null;
}
interface StatusResponse {
  ok: boolean;
  gocardlessConfigured: boolean;
  plans: Record<string, PlanInfo>;
  seatMonthlyPence: number;
  includedSeats: number;
  vatRegistered: boolean;
  subscription: SubscriptionView | null;
}
interface Invoice {
  invoice_number: string;
  period_start: string | null;
  period_end: string | null;
  plan_key: string | null;
  total_pence: number;
  status: string;
  created_at: string;
}
interface InvoicesResponse { ok: boolean; invoices: Invoice[]; }
interface MandateStartResponse { ok: boolean; authorisationUrl?: string; error?: string; }

// ── Helpers ──────────────────────────────────────────────────────────────────
const DATE_FMT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}
function isMissingEndpoint(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 501);
}
function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
function gbp(pence: number): string { return `£${(Math.round(pence) / 100).toFixed(2)}`; }
function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FMT.format(d);
}
function daysUntil(iso: string | null): number {
  if (!iso) return 0;
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return 0;
  return Math.max(0, Math.ceil((d - Date.now()) / 86400000));
}
function monthlyTotalPence(status: StatusResponse, sub: SubscriptionView): number {
  const plan = status.plans[sub.planKey];
  const base = plan ? plan.monthlyPence : 0;
  return base + Math.max(0, sub.seatCount) * status.seatMonthlyPence;
}

// Contextual banner keyed off billing_status.
function bannerFor(status: StatusResponse, sub: SubscriptionView | null): { tone: string; icon: IconName; html: string } {
  if (!status.gocardlessConfigured) {
    return { tone: "muted", icon: "info", html: "Billing isn’t available in this environment yet." };
  }
  const s = sub?.billingStatus ?? "none";
  switch (s) {
    case "exempt":
      return { tone: "muted", icon: "info", html: "This is an internal account — billing doesn’t apply." };
    case "cancelled":
      return { tone: "muted", icon: "info", html: "Your subscription has been cancelled." };
    case "trialing": {
      const left = daysUntil(sub!.trialEndsAt);
      return { tone: "info", icon: "calendar", html: `Free trial — <strong>${left} day${left === 1 ? "" : "s"} left</strong>. First payment ${escapeHtml(fmtDate(sub!.trialEndsAt))}.` };
    }
    case "active":
      return { tone: "success", icon: "check", html: `Your subscription is active. Next payment ${escapeHtml(fmtDate(sub!.nextChargeDate))}.` };
    case "past_due":
      return { tone: "warning", icon: "alert-triangle", html: "We couldn’t collect your last payment. GoCardless will retry automatically — please check your bank details." };
    case "suspended":
      return { tone: "danger", icon: "alert-triangle", html: "Your account is paused — payment outstanding. Set up Direct Debit to restore access. Your customers’ payments are unaffected." };
    case "pending_mandate":
    default:
      return { tone: "amber", icon: "pound", html: "Set up Direct Debit to start your 14-day free trial." };
  }
}

// ── Render ───────────────────────────────────────────────────────────────────
function renderShell(container: HTMLElement): { bannerEl: HTMLElement; bodyEl: HTMLElement } {
  container.innerHTML = `
    <section class="set-billing-root" aria-labelledby="set-billing-title">
      <h2 class="sr-only" id="set-billing-title">Billing</h2>
      <div class="set-billing-banner" data-role="banner"></div>
      <div class="set-billing-body" data-role="body">
        <div class="set-loading">Loading billing…</div>
      </div>
    </section>
  `;
  return {
    bannerEl: container.querySelector<HTMLElement>("[data-role='banner']")!,
    bodyEl: container.querySelector<HTMLElement>("[data-role='body']")!,
  };
}

function renderBanner(host: HTMLElement, status: StatusResponse, sub: SubscriptionView | null): void {
  const b = bannerFor(status, sub);
  host.className = `set-billing-banner set-billing-banner--${b.tone}`;
  host.innerHTML = `<span class="set-billing-banner-icon" aria-hidden="true">${iconMarkup(b.icon, { size: "sm" })}</span><span>${b.html}</span>`;
}

function tile(label: string, value: string, sub: string): string {
  return `
    <div class="set-billing-tile">
      <div class="set-billing-tile-label">${escapeHtml(label)}</div>
      <div class="set-billing-tile-value">${escapeHtml(value)}</div>
      <div class="set-billing-tile-sub">${escapeHtml(sub)}</div>
    </div>`;
}

function renderBody(host: HTMLElement, status: StatusResponse, sub: SubscriptionView | null, invoices: Invoice[]): void {
  // Exempt / cancelled / not-configured: no plan detail, no CTA.
  const bare = !status.gocardlessConfigured || !sub || sub.billingStatus === "exempt" || sub.billingStatus === "cancelled";
  if (bare) {
    host.innerHTML = `<div class="set-empty">Nothing to manage here.</div>`;
    return;
  }

  const plan = status.plans[sub.planKey];
  const planLabel = plan ? plan.label : sub.planKey;
  const planPence = plan ? plan.monthlyPence : 0;
  const totalPence = monthlyTotalPence(status, sub);
  const totalSeats = status.includedSeats + Math.max(0, sub.seatCount);
  const nextDate = sub.billingStatus === "trialing" ? sub.trialEndsAt : sub.nextChargeDate;

  const tiles = `
    <div class="set-billing-tiles">
      ${tile("Plan", `${planLabel} · ${gbp(planPence)}/mo`, "Billed monthly by Direct Debit")}
      ${tile("Seats", `${totalSeats}`, `${status.includedSeats} included${sub.seatCount > 0 ? ` + ${sub.seatCount} @ ${gbp(status.seatMonthlyPence)}` : ""}`)}
      ${tile(sub.billingStatus === "trialing" ? "First payment" : "Next payment", gbp(totalPence), fmtDate(nextDate))}
    </div>`;

  // Direct Debit block: connected badge, or a setup CTA.
  const ddCtaLabel = sub.billingStatus === "suspended" || sub.billingStatus === "past_due" ? "Update Direct Debit" : "Set up Direct Debit";
  const ddBlock = sub.mandateConnected
    ? `<div class="set-billing-dd set-billing-dd--ok">
         <span class="set-billing-badge set-billing-badge--ok">${iconMarkup("check", { size: "sm" })} Connected</span>
         <span class="set-billing-dd-note">Collected automatically by GoCardless Direct Debit.</span>
       </div>`
    : `<div class="set-billing-dd">
         <span class="set-billing-badge set-billing-badge--off">Not set up</span>
         <button type="button" class="set-btn" data-role="setup-mandate">${escapeHtml(ddCtaLabel)} ${iconMarkup("arrow-right", { size: "sm" })}</button>
       </div>`;

  // Invoices table.
  const invRows = invoices.length
    ? invoices.map((iv) => `
        <tr>
          <td><code>${escapeHtml(iv.invoice_number)}</code></td>
          <td>${escapeHtml(iv.period_start ? `${fmtDate(iv.period_start)} – ${fmtDate(iv.period_end)}` : fmtDate(iv.created_at))}</td>
          <td>${escapeHtml(gbp(iv.total_pence))}</td>
          <td><span class="set-billing-inv-status set-billing-inv-status--${escapeHtml(iv.status)}">${escapeHtml(iv.status)}</span></td>
        </tr>`).join("")
    : `<tr><td colspan="4" class="set-billing-inv-empty">No invoices yet.</td></tr>`;

  host.innerHTML = `
    <section class="set-section">
      <h3 class="set-section-title">Your plan</h3>
      ${tiles}
    </section>
    <section class="set-section">
      <h3 class="set-section-title">Direct Debit</h3>
      ${ddBlock}
    </section>
    <section class="set-section">
      <h3 class="set-section-title">Invoices</h3>
      <table class="set-billing-inv">
        <thead><tr><th>Invoice</th><th>Period</th><th>Amount</th><th>Status</th></tr></thead>
        <tbody>${invRows}</tbody>
      </table>
    </section>
  `;
}

// ── Mount ────────────────────────────────────────────────────────────────────
const tab: SettingsTab = {
  id: "billing",
  label: "Billing",
  icon: iconMarkup("pound", { size: "sm" }),

  mount(container: HTMLElement, ctx: PageContext) {
    const ui = renderShell(container);
    let controller: AbortController | null = new AbortController();
    let disposed = false;

    let status: StatusResponse | null = null;
    let invoices: Invoice[] = [];

    // One-time toast on return from the GoCardless hosted flow.
    const mandateResult = ctx.params["mandate"];
    if (mandateResult === "connected") ctx.showToast("Direct Debit set up — your trial has started.", "success");
    else if (mandateResult === "cancelled") ctx.showToast("Direct Debit setup was cancelled.", "info");
    else if (mandateResult === "error") ctx.showToast("Something went wrong setting up Direct Debit. Please try again.", "error");

    const rerender = (): void => {
      if (!status) return;
      renderBanner(ui.bannerEl, status, status.subscription);
      renderBody(ui.bodyEl, status, status.subscription, invoices);
    };

    const load = async (): Promise<void> => {
      const [statusRes, invRes] = await Promise.allSettled([
        ctx.api<StatusResponse>("/subscription/status", { signal: controller!.signal }),
        ctx.api<InvoicesResponse>("/subscription/invoices", { signal: controller!.signal }),
      ]);
      if (disposed) return;

      if (statusRes.status === "fulfilled") {
        status = statusRes.value;
      } else {
        const err = statusRes.reason;
        if (isAbortError(err)) return;
        if (!isMissingEndpoint(err)) ctx.showToast(err instanceof Error ? err.message : "Couldn’t load billing", "error");
        ui.bannerEl.className = "set-billing-banner set-billing-banner--muted";
        ui.bannerEl.innerHTML = `<span>Billing information is unavailable right now.</span>`;
        ui.bodyEl.innerHTML = `<div class="set-empty">Please try again shortly.</div>`;
        return;
      }

      if (invRes.status === "fulfilled" && Array.isArray(invRes.value.invoices)) {
        invoices = invRes.value.invoices;
      }
      rerender();
    };

    void load();

    // ── Mandate setup ──────────────────────────────────────────────────────
    const startMandate = async (): Promise<void> => {
      try {
        const data = await ctx.api<MandateStartResponse>("/subscription/mandate/start", { method: "POST", body: JSON.stringify({}) });
        if (disposed) return;
        if (data.authorisationUrl) {
          window.location.assign(data.authorisationUrl); // GoCardless hosted flow needs a full-page nav
          return;
        }
        ctx.showToast(data.error || "Direct Debit setup isn’t available yet.", "info");
      } catch (err) {
        if (isAbortError(err) || disposed) return;
        if (err instanceof ApiError && err.status === 503) {
          ctx.showToast("Billing isn’t configured for this environment yet.", "info");
          return;
        }
        ctx.showToast(err instanceof Error ? err.message : "Couldn’t start Direct Debit setup", "error");
      }
    };

    const onBodyClick = (e: MouseEvent): void => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      const btn = target.closest<HTMLButtonElement>("button[data-role='setup-mandate']");
      if (btn) void startMandate();
    };
    ui.bodyEl.addEventListener("click", onBodyClick);

    return () => {
      disposed = true;
      controller?.abort();
      controller = null;
      ui.bodyEl.removeEventListener("click", onBodyClick);
    };
  },
};

export default tab;
